import React, { useMemo } from "react";

const MONTH_NAMES = [
	"Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
	"Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];
const DAY_LETTERS = ["L", "M", "M", "J", "V", "S", "D"];

function getDaysInMonth(year, month) {
	return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
	// 1 = Monday … 7 = Sunday (to match grid starting Monday)
	const d = new Date(year, month, 1).getDay();
	return d === 0 ? 7 : d;
}

export default function CalendarPanel({
	currentDate,
	trainingDays,
	selectedDay,
	loading,
	dayTrainings = [],
	dayTrainingsLoading = false,
	upcoming = [],
	onPrevMonth,
	onNextMonth,
	onDayClick,
	onSelectTraining,
	onDuplicateWeek,
}) {
	const year = currentDate.getFullYear();
	const month = currentDate.getMonth();
	const daysInMonth = getDaysInMonth(year, month);
	const firstDay = getFirstDayOfMonth(year, month);

	const cells = useMemo(() => {
		const arr = [];
		for (let i = 1; i < firstDay; i++) arr.push({ kind: "empty", key: `e-${i}` });
		for (let day = 1; day <= daysInMonth; day++) {
			const hasTraining = (trainingDays || []).includes(day);
			const isToday =
				new Date().getDate() === day &&
				new Date().getMonth() === month &&
				new Date().getFullYear() === year;
			const isSelected = selectedDay === day;
			arr.push({ kind: "day", key: `d-${day}`, day, hasTraining, isToday, isSelected });
		}
		return arr;
	}, [firstDay, daysInMonth, trainingDays, selectedDay, month, year]);

	return (
		<div className="editor-panel-calendar">
			<div className="editor-panel-calendar-header">
				<button className="editor-panel-calendar-nav" onClick={onPrevMonth} aria-label="Mois précédent">‹</button>
				<span className="editor-panel-calendar-title">
					{MONTH_NAMES[month]} {year}
				</span>
				<button className="editor-panel-calendar-nav" onClick={onNextMonth} aria-label="Mois suivant">›</button>
			</div>

			{onDuplicateWeek && (
				<button
					type="button"
					className="editor-panel-calendar-dupweek"
					onClick={onDuplicateWeek}
					title="Recopier une semaine complète sur une autre"
				>
					<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
						<rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
						<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
					</svg>
					Dupliquer une semaine
				</button>
			)}

			<div className="editor-panel-calendar-week">
				{DAY_LETTERS.map((letter, i) => (
					<span key={i} className="editor-panel-calendar-week-letter">{letter}</span>
				))}
			</div>

			<div className="editor-panel-calendar-grid">
				{loading ? (
					<div className="editor-panel-calendar-loading">Chargement…</div>
				) : cells.map((c) => (
					c.kind === "empty" ? (
						<div key={c.key} className="editor-panel-calendar-day empty" />
					) : (
						<button
							key={c.key}
							className={`editor-panel-calendar-day ${c.hasTraining ? "has-training" : ""} ${c.isToday ? "today" : ""} ${c.isSelected ? "selected" : ""}`}
							onClick={() => onDayClick?.(c.day)}
						>
							<span className="editor-panel-calendar-day-num">{c.day}</span>
							{c.hasTraining && <span className="editor-panel-calendar-day-dot" />}
						</button>
					)
				))}
			</div>

			{selectedDay != null && (
				<div className="editor-panel-calendar-day-trainings">
					<div className="editor-panel-calendar-day-trainings-header">
						Séances du {selectedDay} {MONTH_NAMES[month].toLowerCase()}
					</div>
					{dayTrainingsLoading ? (
						<div className="editor-panel-calendar-day-empty">Chargement…</div>
					) : (dayTrainings && dayTrainings.length > 0) ? (
						<div className="editor-panel-calendar-day-list">
							{dayTrainings.map((t) => {
								const exos = (t.exercices || []).filter((ex) => ex.supprime !== 1);
								const seriesCount = exos.reduce((sum, ex) => sum + ((ex.series || []).length), 0);
								const MAX_SHOWN = 4;
								return (
									<button
										key={t.id}
										className="editor-panel-calendar-day-item"
										onClick={() => onSelectTraining?.(t)}
									>
										<div className="editor-panel-calendar-day-item-name">
											{t.nom || "Séance"}
										</div>
										<div className="editor-panel-calendar-day-item-meta">
											{exos.length} exo{exos.length > 1 ? "s" : ""}
											{seriesCount > 0 && ` · ${seriesCount} sér.`}
										</div>
										{exos.length > 0 && (
											<ul className="editor-panel-calendar-day-item-exos">
												{exos.slice(0, MAX_SHOWN).map((ex, i) => {
													const exName = ex.nom || ex.nomExercice || "Exercice";
													const nSer = (ex.series || []).length;
													return (
														<li key={ex.localId || ex.id || i}>
															<span className="ex-name">{exName}</span>
															{nSer > 0 && <span className="ex-ser">× {nSer}</span>}
														</li>
													);
												})}
												{exos.length > MAX_SHOWN && (
													<li className="more">+ {exos.length - MAX_SHOWN} autre{exos.length - MAX_SHOWN > 1 ? "s" : ""}</li>
												)}
											</ul>
										)}
									</button>
								);
							})}
						</div>
					) : (
						<div className="editor-panel-calendar-day-empty">
							Aucune séance ce jour
						</div>
					)}
				</div>
			)}

			{upcoming && upcoming.length > 0 && (
				<div className="editor-panel-calendar-upcoming">
					<div className="editor-panel-calendar-upcoming-header">
						À venir · {upcoming.length} séance{upcoming.length > 1 ? "s" : ""}
					</div>
					<div className="editor-panel-calendar-upcoming-list">
						{upcoming.map((s, i) => {
							const d = s.date ? new Date(s.date) : null;
							return (
								<button
									key={s.id || i}
									className="editor-panel-calendar-upcoming-item"
									onClick={() => s.onOpen?.()}
								>
									{d && (
										<div className="editor-panel-calendar-upcoming-date">
											<span className="dow">{d.toLocaleDateString("fr-FR", { weekday: "short" }).replace(".", "")}</span>
											<span className="day">{d.getDate()}</span>
										</div>
									)}
									<div className="editor-panel-calendar-upcoming-info">
										<div className="editor-panel-calendar-upcoming-name">{s.nom || "Séance"}</div>
										{s.subtitle && <div className="editor-panel-calendar-upcoming-sub">{s.subtitle}</div>}
									</div>
								</button>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
}
